import React, {
  useEffect,
  useState
} from "react";
import styled, { css } from "styled-components";
import { HexColorPicker } from "react-colorful";
import { toast } from "react-toastify";

import { media } from "src/library/utils";

import Grid from "../Hedron";
import { Button } from "../Button";
import { Icon } from "../Icon";
import { Icons } from "../Icon/types";
import { Tooltip } from "../Tooltip";
import { Text } from "../Text";
import Input from "./Input";

export interface InputColorProps {
  text?: string;
  hexValue?: string;
  handleChange: (color: string) => void
}

const initialHex = "#000000";
const hexRegex = /^#[0-9A-Fa-f]{6}$/;

/**
 * Input Color Picker Component.
 * Duly note: Currently not hooked into Formik. Used directly.
 */
const InputColor: React.ReactFCWithChildren<InputColorProps> = ({
  text, hexValue, handleChange
}) => {
  const [ color, setColor ] = useState(hexValue || initialHex);
  const [ pickerActive, setPickerActive ] = useState(false);
  const [ hexBeforeConfirm, setHexBeforeConfirm ] = useState(hexValue || initialHex);
  const inputColorRef = React.useRef<HTMLDivElement>(null);

  // update hexBeforeConfirm used when canceling picker element
  useEffect(() => {
    if (hexValue) {
      setHexBeforeConfirm(hexValue);
    }

    // if hexValue not provided on mount, set default in consumer
    if (!hexValue) {
      handleChange(initialHex);
    }
  }, [ hexValue, handleChange ]);

  return (
    <InputColorStyled
      selectedColor={color}
      pickerActive={pickerActive}
    >
      <div
        className="preview"
        onClick={() => setPickerActive(!pickerActive)}
      >
        {text || color}

        <div className="info">
          <div
            className="help-icon"
            data-tip
            data-for="hex-tooltip"
          >
            <Icon
              icon={Icons.HELP}
              size={18}
              $fillColor="white"
            />
          </div>

          <Tooltip
            id="hex-tooltip"
            type="dark"
            place="right"
            getContent={() => {
              return (
                <Text color="white">
                  Click to select a new colour
                </Text>
              );
            }}
          />
        </div>
      </div>

      <div
        className="picker"
        ref={inputColorRef}
      >
        <HexColorPicker
          color={color}
          onChange={setColor}
        />

        <div className="hex-value">
          <Input
            as="input"
            name="hex"
            autoHideLabels
            label=""
            value={color}
            handleChange={e => setColor(e.target.value)}
          />
        </div>

        <div className="cancel-confirm">
          <Button
            $buttonType="secondary"
            onClick={() => {
              setPickerActive(false);
              setColor(hexBeforeConfirm);
            }}
          >
            Cancel
          </Button>

          <Button
            $buttonType="primary"
            onClick={() => {
              // only accept valid hex string
              if (hexRegex.test(color)) {
                setPickerActive(false);
                handleChange(color);
              } else {
                toast.error("Hex input invalid, must be a valid hex string.");
              }
            }}
          >
            Confirm
          </Button>
        </div>
      </div>
    </InputColorStyled>
  );
};

const InputColorStyled = styled(Grid.Box)<{ selectedColor: string, pickerActive: boolean }>`
  ${({
    theme, selectedColor, pickerActive
  }) => css`
  max-width: 250px;

    .preview {
      color: ${theme.colors.white};
      background-color: ${selectedColor};
      border: 2px solid ${theme.colors.lightGrey};
      height: 40px;
      border-radius: ${pickerActive ? `${theme.borderRadius} ${theme.borderRadius} 0 0` : theme.borderRadius};
      display: flex;
      align-items: center;
      padding: 10px 15px;
      display: flex;
      justify-content: space-between;
      font-weight: bold;

      .info {
        opacity: 0;
        transition: opacity 0.1s;

        .help-icon {
          cursor: help;
        }
      }
  
      &:hover {
        border-color: ${theme.colors.midGrey};
        cursor: pointer;

        .info {
          opacity: 1;
          transition: opacity 0.1s;
        }
      }
    }

    .picker {
      display: flex;
      align-items: center;
      flex-direction: column;
      justify-content: center;
      height: ${pickerActive ? "350px" : "0"};
      background: ${theme.colors.white};
      transition: height 0.4s;
      border: ${pickerActive ? "2px" : "0"} solid ${theme.colors.lightGrey};
      border-top: 0;
      border-radius: ${pickerActive ? `0 0 ${theme.borderRadius} ${theme.borderRadius} ` : theme.borderRadius};
      overflow: hidden;

      .hex-value {
        display: flex;
        padding: ${theme.spacing.padding.md} 0;
        
        // forcing Input component to match HexColorPicker width
        .input-wrapper input {
          width: 200px;
        }
      }

      .cancel-confirm {
        width: 200px;
        display: flex;
        flex-direction: row;
        justify-content: space-between;

        ${media({ lt: theme.breakpoints.sm })} {
          flex-direction: column;
    
          > button {
            width: 100%;
            margin-bottom: ${theme.spacing.margins.sm};
          }
        }
      }
    }
  `}
`;

export default InputColor;
